import { useCallback, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';

import { ColDef, GridOptions } from '@ag-grid-community/core';
import { AG_GRID_LOCALE_FR } from '@ag-grid-community/locale';
import { AgGridReact } from '@ag-grid-community/react';
import {
    faCalendar,
    faClock,
    faFilter,
    faMoon,
    faPlus,
    faSun,
    faUsers,
    faUtensils,
} from '@fortawesome/pro-duotone-svg-icons';
import { faCalendarXmark, faCloudBolt } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    IonButton,
    IonContent,
    IonDatetime,
    IonHeader,
    IonModal,
    IonSelect,
    IonSelectOption,
    IonTitle,
    IonToolbar,
    useIonAlert,
    useIonToast,
} from '@ionic/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { entries, filter, groupBy, map, reject, size, sumBy } from 'lodash/fp';
import { DateTime, Duration } from 'luxon';

import '../../pages/validation/ValidationByEmployee.css';
import './ValidationPointage.css';

import {
    adjustMadsPropertiesByTime,
    useAppConfig,
    useEmployees,
    useMads,
    usePointing,
    useWorkPlaces,
} from '../../hooks/useSwr';
import { HistoryStandalone } from '../../pages/History';
import { DisplayMatrixItemType } from '../../pages/validation/ChooseLeaveState';
import { OdooPointing, Pointing, pointingFields } from '../../types/models';
import { defaultCatchOdooError, loadingAtom, pointingToEditIdAtom, usersAtom } from '../../utils/Global';
import { dateFromOdooToFrench, nowToOdooDatetime } from '../../utils/date';
import odoo, { saveOrCreateWithoutRead, unstandardizedOdooPointing } from '../../utils/odoo';
import { LeaveState, PointingState, cleanDataBeforeSending, pointingInitialValue } from '../../utils/odooUtils';
import { LunchConstraints, WorkplaceConstraints } from '../../utils/pointingConstraints';
import { WorkingHoursCellEditor, WorkplaceCellEditor, isPointingEditable } from '../ag-grid/CellEditor';
import { CellValueWithIcon, HoursDisplay } from '../ag-grid/CellRender';
import PointingDetail, { ViewMode } from '../common/PointingDetail';
import { displayMatrixPointage } from '../mobile/ValidationPointage';
import { WebHeader } from './WebHeader';

// Type definition for workplace cell editor value
interface WorkplaceEditorValue {
    workplace_id: [number, string] | false;
    other_workplace: string;
    city?: string;
}

export const StatusBadge = <T extends PointingState | LeaveState>({
    state,
    stateConfig,
}: {
    state: T;
    stateConfig: Record<T, { label: string; className: string }>;
}) => {
    const config = stateConfig[state];

    return (
        <div
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${config.className}`}
        >
            {config.label}
        </div>
    );
};

export const ValidationPointageWeb: React.FC = () => {
    const [presentToast] = useIonToast();
    const gridRef = useRef<AgGridReact>(null);
    const { ids: employeesIds, dataWithDisplayName: employees } = useEmployees();
    const { data: mads } = useMads();
    const { dataWithPlacehoder: workPlaces } = useWorkPlaces();
    const { mainUser } = useAtomValue(usersAtom);
    const { data: appConfig } = useAppConfig();
    const [selectedStates, setSelectedStates] = useState<PointingState[]>([PointingState.WaitValidation]);
    const [selectedPointings, setSelectedPointings] = useState<OdooPointing[]>([]);

    // Add Pointage Modal State
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDateModal, setShowDateModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState<string>(DateTime.now().toISODate()!);
    const [newPointing, setNewPointing] = useState<Pointing | null>(null);
    const addModalRef = useRef<HTMLIonModalElement>(null);
    const dateModalRef = useRef<HTMLIonModalElement>(null);

    // Date filter options
    enum DateFilterOption {
        CurrentMonth = 'current_month',
        LastMonth = 'last_month',
        Last6Months = 'last_6_months',
    }

    const [selectedDateFilter, setSelectedDateFilter] = useState<DateFilterOption>(DateFilterOption.Last6Months);

    // Calculate date range filters based on selected option
    const getDateFilters = (): [string, string, string][] => {
        const now = DateTime.now();
        let startDate: DateTime;
        let endDate: DateTime;

        switch (selectedDateFilter) {
            case DateFilterOption.CurrentMonth:
                startDate = now.startOf('month');
                endDate = now.endOf('month');
                break;
            case DateFilterOption.LastMonth:
                startDate = now.minus({ months: 1 }).startOf('month');
                endDate = now.minus({ months: 1 }).endOf('month');
                break;
            case DateFilterOption.Last6Months:
                startDate = now.minus({ months: 6 }).startOf('month');
                endDate = now.endOf('month');
                break;
            default:
                startDate = now.startOf('month');
                endDate = now.endOf('month');
        }

        return [
            ['date', '>=', startDate.toFormat('yyyy-MM-dd')],
            ['date', '<=', endDate.toFormat('yyyy-MM-dd')],
        ];
    };

    const {
        data: pointings,
        isLoading,
        mutate,
    } = usePointing(employeesIds, [['state', 'in', selectedStates], ['origin_id', '=', false], ...getDateFilters()]);
    const setLoading = useSetAtom(loadingAtom);

    const [validationConfirmAlert] = useIonAlert();
    const [historyPointing, setHistoryPointing] = useState<OdooPointing | null>(null);
    const historyModalRef = useRef<HTMLIonModalElement>(null);
    const [addConfirmAlert] = useIonAlert();

    const columnDefs = useMemo<ColDef<OdooPointing>[]>(
        () => [
            {
                headerName: 'Employé',
                field: 'employee_id',
                filter: true,
                pinned: 'left',
                valueFormatter: (params) => params.value[1],
                cellStyle: {
                    textAlign: 'left',
                },
                minWidth: 150,
                initialWidth: 300,
            },
            {
                headerName: 'Lieu de travail',
                field: 'workplace_id',
                flex: 2,
                minWidth: 180,
                valueGetter: (params: any) => {
                    const { workplace_id, other_workplace } = params.data;
                    // Return display value as string for grid rendering
                    if (workplace_id && Array.isArray(workplace_id) && workplace_id[1]) {
                        return workplace_id[1];
                    }
                    return other_workplace || '';
                },
                valueSetter: (params: any) => {
                    const { newValue, data } = params;
                    // WorkplaceCellEditor returns a WorkplaceEditorValue object
                    if (typeof newValue === 'object' && newValue !== null) {
                        const workplaceValue = newValue as WorkplaceEditorValue;
                        // Store the complete workplace value in a special property for handleCellValueChanged
                        data._workplaceEditorValue = workplaceValue;
                        // Update the row data with the workplace object for display
                        data.workplace_id = workplaceValue.workplace_id;
                        data.other_workplace = workplaceValue.other_workplace || '';
                        if (workplaceValue.city !== undefined) {
                            data.city = workplaceValue.city;
                        }
                        return true;
                    }
                    return false;
                },
                cellStyle: { textAlign: 'left' },
                editable: isPointingEditable(),
                cellEditor: WorkplaceCellEditor,
                cellEditorPopup: true,
            },
            {
                headerName: 'Ville',
                field: 'city',
                flex: 1,
                minWidth: 120,
                valueGetter: (params: any) => params.data.city || '',
                cellStyle: { textAlign: 'left' },
                editable: isPointingEditable(['workplace_id']),
            },
            {
                headerName: 'Date',
                field: 'date',
                flex: 2,
                valueFormatter: (params) => dateFromOdooToFrench(params.value),
                editable: isPointingEditable(),
                sort: 'asc',
            },
            {
                headerName: 'Heures',
                children: [
                    {
                        headerName: 'Jour',
                        field: 'working_hours',
                        flex: 2,
                        minWidth: 95,
                        editable: isPointingEditable(),
                        cellEditor: WorkingHoursCellEditor,
                        cellRenderer: (params: any) => {
                            const { working_hours } = params.data;
                            return (
                                <HoursDisplay
                                    value={working_hours}
                                    icon={faSun}
                                    colorClass="text-yellow-500"
                                    label="Jour"
                                />
                            );
                        },
                    },
                    {
                        headerName: 'Nuit',
                        field: 'working_night_hours',
                        flex: 2,
                        minWidth: 95,
                        editable: isPointingEditable(),
                        cellEditor: WorkingHoursCellEditor,
                        cellRenderer: (params: any) => {
                            const { working_night_hours } = params.data;
                            return (
                                <HoursDisplay
                                    value={working_night_hours}
                                    icon={faMoon}
                                    colorClass="text-indigo-500"
                                    label="Nuit"
                                />
                            );
                        },
                    },
                    {
                        headerName: 'Intempérie',
                        field: 'bad_weather_hours',
                        flex: 2,
                        minWidth: 95,
                        editable: isPointingEditable(),
                        cellEditor: WorkingHoursCellEditor,
                        cellRenderer: (params: any) => {
                            const { bad_weather_hours } = params.data;
                            return (
                                <HoursDisplay
                                    value={bad_weather_hours}
                                    icon={faCloudBolt}
                                    colorClass="text-blue-400"
                                    label="Intempéries"
                                />
                            );
                        },
                    },
                    {
                        headerName: 'Absence',
                        field: 'absence_hours',
                        flex: 2,
                        minWidth: 95,
                        editable: isPointingEditable(),
                        cellEditor: WorkingHoursCellEditor,
                        cellRenderer: (params: any) => {
                            const { absence_hours } = params.data;
                            return (
                                <HoursDisplay
                                    value={absence_hours}
                                    icon={faCalendarXmark}
                                    colorClass="text-red-400"
                                    label="Absence"
                                />
                            );
                        },
                    },
                ],
            },
            {
                headerName: 'Paniers',
                marryChildren: true,
                children: [
                    {
                        headerName: 'Jour',
                        field: 'lunch',
                        flex: 1,
                        minWidth: 60,
                        editable: isPointingEditable(),
                        cellRenderer: (params: any) => {
                            const { lunch } = params.data;
                            return (
                                <CellValueWithIcon
                                    value={lunch}
                                    icon={faUtensils}
                                    colorClass="text-orange-600"
                                    label="Panier jour"
                                />
                            );
                        },
                    },
                    {
                        headerName: 'Nuit',
                        field: 'lunch_night',
                        flex: 1,
                        minWidth: 60,
                        editable: isPointingEditable(),
                        cellRenderer: (params: any) => {
                            const { lunch_night } = params.data;
                            return (
                                <CellValueWithIcon
                                    value={lunch_night}
                                    icon={faUtensils}
                                    colorClass="text-indigo-500"
                                    label="Panier nuit"
                                />
                            );
                        },
                    },
                ],
            },
            {
                headerName: 'Note',
                children: [
                    {
                        headerName: 'Emp.',
                        field: 'employee_note',
                        flex: 2,
                        cellStyle: { textAlign: 'left' },
                        tooltipField: 'employee_note',
                    },
                    {
                        headerName: 'Adhérent',
                        field: 'adherent_note',
                        flex: 2,
                        cellStyle: { textAlign: 'left' },
                        tooltipField: 'adherent_note',
                        editable: isPointingEditable(),
                        cellEditor: 'agLargeTextCellEditor',
                        cellEditorPopup: true,
                        valueGetter: (params: any) => {
                            // Display empty string when value is false (Odoo's way of representing empty text fields)
                            return params.data.adherent_note === false ? '' : params.data.adherent_note || '';
                        },
                    },
                ],
            },
            {
                headerName: 'Status',
                field: 'state',
                width: 120,
                pinned: 'right',
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px',
                }),
                cellRenderer: (params: any) => {
                    const orangeClassName = 'bg-orange-50 text-orange-700 border-orange-200';
                    const greenClassName = 'bg-green-50 text-green-700 border-green-200';
                    const blueClassName = 'bg-blue-50 text-blue-700 border-blue-200';

                    return (
                        <StatusBadge
                            state={params.value as PointingState}
                            stateConfig={{
                                [PointingState.Draft]: {
                                    label: 'Non saisi',
                                    className: blueClassName,
                                },
                                [PointingState.StartPointing]: {
                                    label: 'En cours',
                                    className: blueClassName,
                                },
                                [PointingState.EndPointing]: {
                                    label: 'Terminé',
                                    className: blueClassName,
                                },
                                [PointingState.WaitValidation]: {
                                    label: 'À valider',
                                    className: orangeClassName,
                                },
                                [PointingState.AdherentValidation]: {
                                    label: 'Validé',
                                    className: greenClassName,
                                },
                                [PointingState.GeiqValidation]: {
                                    label: 'Validé GEIQ',
                                    className: greenClassName,
                                },
                            }}
                        />
                    );
                },
            },
            {
                headerName: 'Actions',
                pinned: 'right',
                width: 150,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4px',
                }),
                cellRenderer: (params: any) => (
                    <button
                        type="button"
                        className="gap-1 px-3 py-1 rounded-full border border-gray-300 text-xs text-gray-700 bg-gray-100 hover:bg-gray-300 bg-gray-100 transition font-medium shadow-sm justify-center h-full"
                        onClick={(e) => {
                            e.stopPropagation();
                            setHistoryPointing(params.data);
                            setTimeout(() => historyModalRef.current?.present(), 0);
                        }}
                    >
                        <FontAwesomeIcon icon={faClock} className="w-3 h-3 mr-1 text-gray-400" />
                        Historique
                    </button>
                ),
            },
        ],
        [],
    );

    // Configuration par défaut des colonnes
    const defaultColDef = useMemo(
        () => ({
            sortable: true,
            filter: false,
            resizable: true,
            minWidth: 100,
            cellStyle: { textAlign: 'center' },
        }),
        [],
    );

    // Configuration de la grille
    const gridOptions = useMemo<GridOptions>(
        () => ({
            rowSelection: {
                headerCheckbox: true,
                mode: 'multiRow',
                isRowSelectable: (node) => node.data?.state === PointingState.WaitValidation,
            },
            pagination: true,
            paginationPageSize: 50,
            paginationPageSizeSelector: [50, 100, 200],
            localeText: AG_GRID_LOCALE_FR,
            selectionColumnDef: {
                pinned: 'left',
            },
        }),
        [],
    );

    const onRowClicked = useCallback((event: any) => {
        const {
            event: { target },
            node,
        } = event;

        if (target.closest('[role="gridcell"]').getAttribute('col-id') == 'ag-Grid-ControlsColumn') {
            node.setSelected(!node.isSelected());
            return;
        }
    }, []);

    const getPointingDetails = useCallback(() => {
        const pointingToValidate = filter((pointing) => pointing.state === PointingState.WaitValidation, pointings);
        const totalEmployees = size(groupBy('employee_id', pointingToValidate));
        const totalMinutes = sumBy(
            (pointing) =>
                (pointing.working_hours +
                    pointing.working_night_hours +
                    pointing.bad_weather_hours +
                    pointing.absence_hours) *
                60,
            pointingToValidate,
        );
        const totalPaniers = sumBy((pointing) => pointing.lunch + pointing.lunch_night, pointingToValidate);

        const humanizeTime = (minutes: number) => {
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = Math.round(minutes % 60);
            return Duration.fromObject({ hours, minutes: remainingMinutes }).toHuman();
        };

        return {
            hasData: pointingToValidate.length > 0,
            count: pointingToValidate.length,
            employees: totalEmployees,
            timeText: humanizeTime(totalMinutes),
            paniers: totalPaniers,
        };
    }, [pointings]);

    const revertCellValue = useCallback(
        (pointingId: number, field: string, oldValue: any) => {
            if (gridRef.current) {
                const rowNode = gridRef.current.api.getRowNode(pointingId.toString());
                if (rowNode) {
                    rowNode.setDataValue(field, oldValue);
                }
            }
        },
        [gridRef],
    );

    const prepareUpdateData = useCallback((field: string, newValue: any, data: any): any => {
        // Check if this is a workplace change by looking for the stored workplace editor value
        if (field === 'workplace_id' && data._workplaceEditorValue) {
            const workplaceValue = data._workplaceEditorValue as WorkplaceEditorValue;
            // Clear the temporary property
            delete data._workplaceEditorValue;

            // Use shared constraint to prepare workplace data
            return WorkplaceConstraints.prepareWorkplaceUpdateData(workplaceValue);
        }

        return { [field]: newValue };
    }, []);

    const handleCellValueChanged = useCallback(
        async (event: any) => {
            const { data, colDef, newValue, oldValue } = event;

            if (newValue === oldValue) return;

            const field = colDef.field;
            const pointingId = data.id;

            // Validate lunch constraint
            const lunchValidation = LunchConstraints.validateLunchConstraint(
                appConfig,
                field,
                newValue,
                data.lunch,
                data.lunch_night,
            );

            if (!lunchValidation.isValid) {
                revertCellValue(pointingId, field, oldValue);
                presentToast({
                    message: lunchValidation.errorMessage || 'Validation error',
                    duration: 4000,
                    color: 'danger',
                });
                return;
            }

            try {
                setLoading(true);
                const updateData = prepareUpdateData(field, newValue, data);

                await odoo.write('hr.madper.app.pointing.time', pointingId, updateData, {
                    context: { bypassValidation: true },
                });

                mutate();
                presentToast({
                    message: 'Modification enregistrée avec succès',
                    duration: 2000,
                    color: 'success',
                });
            } catch (e) {
                revertCellValue(pointingId, field, oldValue);
                defaultCatchOdooError(e, presentToast);
            } finally {
                setLoading(false);
            }
        },
        [appConfig, revertCellValue, prepareUpdateData, mutate, presentToast, setLoading],
    );

    const handleValidation = useCallback(async () => {
        if (!gridRef.current) {
            console.error('gridRef.current is null');
            return;
        }

        const selectedRows = gridRef.current.api.getSelectedRows();
        if (!selectedRows.length) return;

        validationConfirmAlert({
            header: 'Veuillez confirmer la validation',
            buttons: [
                {
                    text: 'Annuler',
                    role: 'cancel',
                },
                {
                    text: 'Valider',
                    role: 'confirm',
                    cssClass: 'accept-button',
                    handler: async () => {
                        try {
                            setLoading(true);
                            await Promise.all(
                                selectedRows.map((pointing: OdooPointing) =>
                                    odoo.write('hr.madper.app.pointing.time', pointing.id, {
                                        user_adherent_id: mainUser!.uid,
                                        user_adherent_validation_date: nowToOdooDatetime(),
                                        ...(pointing.adherent_note && {
                                            adherent_note: pointing.adherent_note,
                                        }),
                                    }),
                                ),
                            );
                            mutate();
                            gridRef.current?.api.deselectAll();
                            setLoading(false);
                        } catch (e) {
                            defaultCatchOdooError(e, presentToast);
                        }
                    },
                },
            ],
        });
    }, [gridRef, mainUser, presentToast, validationConfirmAlert, mutate, setLoading]);

    const handleAddPointing = useCallback(() => {
        setSelectedDate(DateTime.now().toISODate()!);
        setShowDateModal(true);
        setTimeout(() => dateModalRef.current?.present(), 0);
    }, []);

    const handleDateConfirm = useCallback(() => {
        const selectedDateTime = DateTime.fromISO(selectedDate);
        const initialPointing = pointingInitialValue(
            selectedDate,
            reject('disabled', adjustMadsPropertiesByTime(mads, selectedDateTime)),
            workPlaces,
            employees,
        );
        setNewPointing(initialPointing);
        setShowDateModal(false);
        setShowAddModal(true);
        setTimeout(() => addModalRef.current?.present(), 0);
    }, [selectedDate, mads, workPlaces, employees]);

    const handleSaveNewPointing = useCallback(async () => {
        if (!newPointing) return;

        addConfirmAlert({
            header: 'Confirmer la création',
            message: 'Voulez-vous créer ce nouveau pointage ?',
            buttons: [
                {
                    text: 'Annuler',
                    role: 'cancel',
                },
                {
                    text: 'Créer',
                    role: 'confirm',
                    cssClass: 'accept-button',
                    handler: async () => {
                        try {
                            setLoading(true);
                            const cleanedPointing = cleanDataBeforeSending(newPointing);
                            const pointingToSave = unstandardizedOdooPointing(cleanedPointing);

                            await saveOrCreateWithoutRead('hr.madper.app.pointing.time', pointingToSave, {
                                bypassValidation: true,
                            });

                            mutate();
                            setShowAddModal(false);
                            setNewPointing(null);

                            presentToast({
                                message: 'Pointage créé avec succès',
                                duration: 2000,
                                color: 'success',
                            });
                        } catch (e) {
                            defaultCatchOdooError(e, presentToast);
                        } finally {
                            setLoading(false);
                        }
                    },
                },
            ],
        });
    }, [newPointing, addConfirmAlert, setLoading, mutate, presentToast]);

    const pointingDetails = getPointingDetails();

    return (
        <>
            <WebHeader title="Validation des pointages" />
            <IonContent className="ion-padding bg-slate-100">
                <div style={{ height: 'calc(100% - 56px)', display: 'flex', flexDirection: 'column' }}>
                    <div className="mb-4 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                            <div className="flex flex-col">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-800">Saisies de temps à valider</h3>
                                </div>
                                <div className="flex items-center justify-center space-x-6">
                                    <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-md">
                                        <FontAwesomeIcon icon={faUsers} className="text-blue-600" />
                                        <span className="text-sm font-medium text-gray-700">
                                            {pointingDetails.hasData
                                                ? `${pointingDetails.count} pointages · ${pointingDetails.employees} employés`
                                                : '-'}
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-md">
                                        <FontAwesomeIcon icon={faClock} className="text-green-600" />
                                        <span className="text-sm font-medium text-gray-700">
                                            {pointingDetails.hasData ? pointingDetails.timeText : '-'}
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-2 bg-white rounded-lg px-3 py-2 shadow-md">
                                        <FontAwesomeIcon icon={faUtensils} className="text-orange-600" />
                                        <span className="text-sm font-medium text-gray-700">
                                            {pointingDetails.hasData && pointingDetails.paniers > 0
                                                ? `${pointingDetails.paniers} paniers`
                                                : '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <div className="flex items-center space-x-2">
                                    <FontAwesomeIcon icon={faCalendar} className="text-gray-500" />
                                    <span className="text-sm font-medium text-gray-700">Période :</span>
                                </div>
                                <IonSelect
                                    className="filter-select"
                                    style={{
                                        '--border-radius': '8px',
                                        '--background': '#f8fafc',
                                        '--color': '#374151',
                                        '--padding-start': '14px',
                                        width: '200px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                    }}
                                    interface="popover"
                                    value={selectedDateFilter}
                                    onIonChange={(e) => {
                                        setSelectedDateFilter(e.detail.value);
                                    }}
                                    placeholder="Choisir une période..."
                                >
                                    <IonSelectOption value={DateFilterOption.CurrentMonth}>Mois actuel</IonSelectOption>
                                    <IonSelectOption value={DateFilterOption.LastMonth}>Mois dernier</IonSelectOption>
                                    <IonSelectOption value={DateFilterOption.Last6Months}>
                                        6 derniers mois
                                    </IonSelectOption>
                                </IonSelect>
                                <div className="flex items-center space-x-2">
                                    <FontAwesomeIcon icon={faFilter} className="text-gray-500" />
                                    <span className="text-sm font-medium text-gray-700">Filtrer par statut :</span>
                                </div>
                                <IonSelect
                                    className="filter-select"
                                    style={{
                                        '--border-radius': '8px',
                                        '--background': '#f8fafc',
                                        '--color': '#374151',
                                        '--padding-start': '14px',
                                        width: '200px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                    }}
                                    interface="popover"
                                    interfaceOptions={{
                                        header: 'Sélectionner les statuts à afficher',
                                    }}
                                    value={selectedStates}
                                    onIonChange={(e) => {
                                        setSelectedStates(e.detail.value);
                                    }}
                                    multiple={true}
                                    cancelText="Annuler"
                                    okText="Valider"
                                    placeholder="Choisir les statuts..."
                                >
                                    {map(
                                        ([state, data]: [PointingState, DisplayMatrixItemType]) => (
                                            <IonSelectOption key={state} value={state}>
                                                {data.title}
                                            </IonSelectOption>
                                        ),
                                        entries(displayMatrixPointage).filter(([key]) => key !== 'withoutPointingTime'),
                                    )}
                                </IonSelect>
                            </div>
                        </div>
                    </div>
                    <div className="validation-pointage-grid ag-theme-alpine" style={{ flex: 1, width: '100%' }}>
                        <AgGridReact
                            rowData={pointings}
                            columnDefs={columnDefs}
                            tooltipShowDelay={0}
                            tooltipHideDelay={500}
                            defaultColDef={defaultColDef}
                            gridOptions={gridOptions}
                            onRowClicked={onRowClicked}
                            onCellValueChanged={handleCellValueChanged}
                            enableCellTextSelection={true}
                            ref={gridRef}
                            getRowId={(params) => params.data.id.toString()}
                            loading={isLoading}
                            stopEditingWhenCellsLoseFocus={true}
                            onSelectionChanged={() => {
                                setSelectedPointings(gridRef.current?.api?.getSelectedRows() || []);
                            }}
                        />
                    </div>
                    <div className="madper-button flex gap-4">
                        <IonButton shape="round" className="mt-6 mb-0" color="success" onClick={handleAddPointing}>
                            <FontAwesomeIcon icon={faPlus} className="mr-2" />
                            Ajouter un pointage
                        </IonButton>
                        <IonButton
                            shape="round"
                            className="mt-6 mb-0"
                            onClick={handleValidation}
                            disabled={size(filter({ state: PointingState.WaitValidation }, selectedPointings)) < 1}
                        >
                            {size(filter({ state: PointingState.WaitValidation }, selectedPointings)) > 1
                                ? 'Valider les pointages sélectionnés'
                                : 'Valider le pointage sélectionné'}
                        </IonButton>
                    </div>
                </div>
            </IonContent>
            <IonModal ref={historyModalRef} isOpen={!!historyPointing} onDidDismiss={() => setHistoryPointing(null)}>
                {historyPointing && <HistoryStandalone pointingId={historyPointing.id} viewMode={ViewMode.Adherent} />}
            </IonModal>

            <IonModal ref={dateModalRef} isOpen={showDateModal} onDidDismiss={() => setShowDateModal(false)}>
                <IonHeader>
                    <IonToolbar>
                        <IonTitle>Sélectionner la date</IonTitle>
                    </IonToolbar>
                </IonHeader>
                <IonContent className="ion-padding">
                    <div className="flex flex-col items-center justify-center h-full">
                        <p className="text-lg font-medium text-gray-700 mb-6 text-center">
                            Pour quelle date souhaitez-vous ajouter un pointage ?
                        </p>
                        <IonDatetime
                            value={selectedDate}
                            onIonChange={(e) => setSelectedDate(e.detail.value as string)}
                            presentation="date"
                            locale="fr-FR"
                            className="mb-8"
                        />
                        <div className="flex gap-4">
                            <IonButton shape="round" color="medium" onClick={() => setShowDateModal(false)}>
                                Annuler
                            </IonButton>
                            <IonButton shape="round" color="success" onClick={handleDateConfirm}>
                                Continuer
                            </IonButton>
                        </div>
                    </div>
                </IonContent>
            </IonModal>

            <IonModal
                ref={addModalRef}
                isOpen={showAddModal}
                onDidDismiss={() => {
                    setShowAddModal(false);
                    setNewPointing(null);
                }}
            >
                {newPointing && (
                    <>
                        <IonHeader>
                            <IonToolbar>
                                <IonTitle>Ajouter un pointage</IonTitle>
                            </IonToolbar>
                        </IonHeader>
                        <IonContent className="ion-padding">
                            <PointingDetail
                                initialPointing={newPointing}
                                viewMode={ViewMode.Adherent}
                                onChange={(pointing) => setNewPointing(pointing)}
                            />
                            <div className="madper-button flex gap-4 mt-6">
                                <IonButton
                                    shape="round"
                                    color="medium"
                                    onClick={() => {
                                        setShowAddModal(false);
                                        setNewPointing(null);
                                    }}
                                >
                                    Annuler
                                </IonButton>
                                <IonButton shape="round" color="success" onClick={handleSaveNewPointing}>
                                    Créer le pointage
                                </IonButton>
                            </div>
                        </IonContent>
                    </>
                )}
            </IonModal>
        </>
    );
};

export default ValidationPointageWeb;
